import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { CheckCircle2, XCircle, Loader2, Landmark, Plus } from "lucide-react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";

export default function OrdinanceChecker({ open, onOpenChange, blueprint, city, setCity, lot, stories: storiesProp, floorHeight }) {
  const [ordinances, setOrdinances] = useState([]);
  const [selected, setSelected] = useState("");
  const [lotW, setLotW] = useState(60);
  const [lotD, setLotD] = useState(100);
  const [stories, setStories] = useState(1);
  const [floorHt, setFloorHt] = useState(10);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ city_name: "", max_height_ft: 35, front_setback_ft: 20, side_setback_ft: 5, rear_setback_ft: 15, max_lot_coverage_pct: 50, max_far: 0.6, max_bedrooms: 6 });

  useEffect(() => {
    if (!open) return;
    if (lot?.w) setLotW(Math.round(lot.w));
    if (lot?.h) setLotD(Math.round(lot.h));
    if (storiesProp) setStories(storiesProp);
    if (floorHeight) setFloorHt(floorHeight);
    /* eslint-disable-next-line */
  }, [open]);

  const load = async () => {
    try {
      const res = await api.get("/ordinances");
      setOrdinances(res.data);
      if (res.data.length && !selected) {
        const match = res.data.find((o) => o.city_name === city) || res.data[0];
        setSelected(match.id);
      }
    } catch { toast.error("Failed to load ordinances"); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const runCheck = async () => {
    if (!selected) { toast.error("Pick a city ordinance"); return; }
    setLoading(true); setResult(null);
    try {
      const res = await api.post("/ordinances/check", {
        blueprint, ordinance_id: selected, lot_width_ft: Number(lotW), lot_depth_ft: Number(lotD), stories: Number(stories), floor_height_ft: Number(floorHt),
      });
      setResult(res.data);
      const ord = ordinances.find((o) => o.id === selected);
      if (ord) setCity(ord.city_name);
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
    finally { setLoading(false); }
  };

  const addCustom = async () => {
    if (!form.city_name.trim()) { toast.error("Name your ordinance"); return; }
    try {
      const { city_name, ...rules } = form;
      const res = await api.post("/ordinances", { city_name, rules: { ...rules, min_egress_window_sqft: 5.7 } });
      toast.success("Ordinance added");
      await load();
      setSelected(res.data.id);
      setForm({ ...form, city_name: "" });
    } catch { toast.error("Failed to add"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-xl">
            <Landmark className="w-5 h-5 text-[#F59E0B]" /> City Ordinance Compliance
          </DialogTitle>
          <DialogDescription className="text-[#8B949E]">Validate your plan against preset or custom city building codes.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="check">
          <TabsList className="bg-[#0D1117] border border-[#30363D]">
            <TabsTrigger value="check" data-testid="tab-check">Run Check</TabsTrigger>
            <TabsTrigger value="add" data-testid="tab-add">Add City Rules</TabsTrigger>
          </TabsList>

          <TabsContent value="check" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-[#8B949E]">City Ordinance</Label>
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger data-testid="ordinance-city-select" className="bg-[#0D1117] border-[#30363D]"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent className="bg-[#161B22] border-[#30363D] text-[#F0F6FC]">
                    {ordinances.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.city_name}{o.is_preset ? "" : " (custom)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#8B949E]">Lot Width (ft)</Label>
                <Input type="number" value={lotW} onChange={(e) => setLotW(e.target.value)} className="bg-[#0D1117] border-[#30363D]" data-testid="lot-width-input" />
              </div>
              <div>
                <Label className="text-xs text-[#8B949E]">Lot Depth (ft)</Label>
                <Input type="number" value={lotD} onChange={(e) => setLotD(e.target.value)} className="bg-[#0D1117] border-[#30363D]" data-testid="lot-depth-input" />
              </div>
              <div>
                <Label className="text-xs text-[#8B949E]">Stories</Label>
                <Input type="number" value={stories} onChange={(e) => setStories(e.target.value)} className="bg-[#0D1117] border-[#30363D]" data-testid="stories-input" />
              </div>
              <div>
                <Label className="text-xs text-[#8B949E]">Floor Height (ft)</Label>
                <Input type="number" step="0.5" value={floorHt} onChange={(e) => setFloorHt(e.target.value)} className="bg-[#0D1117] border-[#30363D]" data-testid="floor-height-check-input" />
              </div>
            </div>
            <Button data-testid="ordinance-run-check-btn" onClick={runCheck} disabled={loading} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-[#0D1117] font-semibold">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking…</> : "Run Compliance Check"}
            </Button>

            {result && (
              <div className="space-y-2 animate-fade-up" data-testid="ordinance-results">
                <div className={`text-sm font-semibold ${result.compliant ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {result.compliant ? "✓ Fully Compliant" : `${result.passed}/${result.total} checks passed`} — {result.city_name}
                </div>
                {result.checks.map((c, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-md border border-[#30363D] bg-[#0D1117] p-2.5">
                    {c.pass ? <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" />}
                    <div>
                      <div className="text-sm font-medium">{c.label}</div>
                      <div className="text-xs text-[#8B949E] font-mono-cad">{c.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="add" className="space-y-3 pt-4">
            <div>
              <Label className="text-xs text-[#8B949E]">City / Rule Set Name</Label>
              <Input value={form.city_name} onChange={(e) => setForm({ ...form, city_name: e.target.value })} placeholder="e.g. Portland, OR" className="bg-[#0D1117] border-[#30363D]" data-testid="new-ordinance-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["max_height_ft", "Max Height (ft)"], ["front_setback_ft", "Front Setback (ft)"], ["side_setback_ft", "Side Setback (ft)"], ["rear_setback_ft", "Rear Setback (ft)"], ["max_lot_coverage_pct", "Max Lot Coverage %"], ["max_far", "Max FAR"], ["max_bedrooms", "Max Bedrooms"]].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs text-[#8B949E]">{label}</Label>
                  <Input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} className="bg-[#0D1117] border-[#30363D]" />
                </div>
              ))}
            </div>
            <Button onClick={addCustom} className="w-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="add-ordinance-btn">
              <Plus className="w-4 h-4 mr-1" /> Save City Ordinance
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
